import {
  Button,
  DismissIcon,
  Loading,
  SearchField,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { Form } from 'react-router';

import { BloomLink } from '@jane/bloom/components/linkWithBasename';
import { SearchManager } from '@jane/bloom/components/searchManager';
import { useDebounce } from '@jane/bloom/hooks/useDebounce';
import { SEARCH_TERM_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';
import { sleep } from '@jane/bloom/utils/sleep';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';

import { AutocompleteProductCard } from './autocompleteProductCard';
import styles from './searchSuggestions.module.css';

interface SearchSuggestionsProps {
  hideSearchSuggestions: (persist?: boolean) => void;
  janeGold?: JaneGold;
  placeholder?: string;
  searchModalQuery: string;
  setSearchModalQuery: (query: string) => void;
  /** Classname to apply animations with CSS transitions. */
  transition: string;
}

export function SearchSuggestions({
  hideSearchSuggestions,
  janeGold,
  placeholder,
  searchModalQuery,
  setSearchModalQuery,
  transition,
}: SearchSuggestionsProps) {
  const debouncedSearchModalQuery = useDebounce(searchModalQuery, 300);

  return (
    <div
      className={clsx(styles['searchSuggestionsContainer'], transition, {
        [styles['emptyQuery']]: !searchModalQuery,
      })}
    >
      <div className={styles['mobileSearchHeader']}>
        <div className={styles['mobileSearchHeading']}>
          <div className={styles['dismissIcon']}>
            <Button.Icon
              ariaLabel="Go back"
              icon={<DismissIcon />}
              onClick={() => {
                setSearchModalQuery('');
                hideSearchSuggestions();
              }}
            />
          </div>
          <div className={styles['searchText']}>
            <Typography variant="header-bold">Search</Typography>
          </div>
        </div>
        <Form
          action="/search"
          method="get"
          className={styles['searchFieldBox']}
          name="Search Form"
          onSubmit={async () => {
            /** Pause a beat to let the page navigation progress before hiding the modal */
            await sleep(250);
            hideSearchSuggestions();
          }}
        >
          <SearchField
            borderRadius="lg"
            disableMobileInputStyling
            isDebounced={false}
            label="Search"
            name={SEARCH_TERM_QUERY_KEY}
            onChange={(val) => setSearchModalQuery(val)}
            placeholder={placeholder}
            value={searchModalQuery}
          />
        </Form>
      </div>
      <SearchManager
        /**
         * NB: re-render when query changes, to reset the internal fetcher's old search results and page counter.
         */
        key={debouncedSearchModalQuery}
        query={debouncedSearchModalQuery}
      >
        {({ products, shouldFetchNextPage, totalProducts }) => (
          <>
            {searchModalQuery && (
              <div
                className={clsx({
                  [styles['productsList--loading']]: shouldFetchNextPage,
                })}
              >
                <BloomLink
                  className={styles['searchQuery']}
                  onClick={() => hideSearchSuggestions()}
                  to={`/search?${new URLSearchParams({
                    [SEARCH_TERM_QUERY_KEY]: searchModalQuery,
                  })}`}
                >
                  Search products for “
                  <mark className={styles['searchQuery__mark']}>
                    {searchModalQuery}
                  </mark>
                  ” {totalProducts !== undefined && `(${totalProducts})`}
                </BloomLink>

                {products.map((product) => (
                  <AutocompleteProductCard
                    key={product.attributes.product_id}
                    product={product}
                    query={searchModalQuery}
                    hideSearchSuggestions={hideSearchSuggestions}
                    janeGold={janeGold}
                  />
                ))}

                {shouldFetchNextPage && (
                  <div className={styles['waveyLoading']}>
                    <Loading color="black" />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </SearchManager>
    </div>
  );
}
